import axios, { AxiosInstance } from 'axios';
import Cookies, { CookieChangeOptions } from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';
import mem from 'mem';

export interface ConnectionHandlerConfig {
  baseUrl: string;
  domain: string;
  profile?: string;
  tokenRefreshPath?: string;
}

export type ConnectionType = AxiosInstance & {
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
};

export class ConnectionHandler {
  private baseUrl: string;
  private publicConnection: AxiosInstance;
  public axiosInstance: ConnectionType;
  private refreshToken?: string;
  private accessToken?: string;
  private accessTokenName: string;
  private refreshTokens: () => Promise<{
    refreshToken: string;
    accessToken: string;
  }>;
  private tokenRefreshPath: string = '/auth/refresh-token';
  private cookies: Cookies;
  private accessTokenExp: number = 0;

  constructor(private readonly onUserChange: (user: any) => void) {}

  init = async (config: ConnectionHandlerConfig) => {
    const { baseUrl, domain, profile, tokenRefreshPath } = config;
    if (tokenRefreshPath) {
      this.tokenRefreshPath = tokenRefreshPath;
    }
    this.cookies = new Cookies(null, { path: '/', domain });
    this.baseUrl = baseUrl;
    this.accessTokenName = profile ? `accessToken-${profile}` : 'accessToken';
    this.publicConnection = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    }) as any;

    this.axiosInstance.login = this.login;
    this.axiosInstance.logout = this.logout;

    this.cookies.addChangeListener(this.onCookieChange);

    this.addInterceptors();

    this.refreshTokens = mem(this.refreshTokenFn, { maxAge: 10000 });
    console.log('XXX1');
    const refreshToken = this.cookies.get('refreshToken');
    if (!refreshToken) {
      console.log('XXX2');
      return false;
    }
    try {
      const exp = (jwtDecode(refreshToken).exp ?? 0) * 1000;
      if (exp < Date.now() - 20000) {
        console.log('XXX3');
        return false;
      }
    } catch (e) {
      console.log('XXX4');
      return false;
    }
    this.refreshToken = refreshToken;

    const accessToken = this.cookies.get(this.accessTokenName);
    if (!accessToken) {
      await this.refreshTokens();
      console.log('XXX5');
      return true;
    }
    try {
      const exp = (jwtDecode(accessToken).exp ?? 0) * 1000;
      if (exp < Date.now() - 20000) {
        await this.refreshTokens();
        console.log('XXX6');
        return true;
      }
      this.setAccessToken(accessToken);
    } catch (e) {
      console.log('XXX7');
      return false;
    }
    console.log('XXX8');
    return true;
  };

  login = (accessToken: string, refreshToken: string) => {
    this.cookies.set(this.accessTokenName, accessToken);
    this.cookies.set('refreshToken', refreshToken);
    this.refreshToken = refreshToken;
    this.setAccessToken(accessToken);
  };

  close = () => {
    if (this.cookies) {
      this.cookies.removeChangeListener(this.onCookieChange);
    }
  };

  onCookieChange = (options: CookieChangeOptions) => {
    if (options.name === this.accessTokenName) {
      console.log('accessToken', options.value);
      this.setAccessToken(options.value);
    } else if (options.name === 'refreshToken') {
      console.log('refreshToken', options.value);
      this.refreshToken = options.value;
    } else {
      console.log('unknown cookie', options.value);
    }
  };

  setAccessToken = (accessToken?: string) => {
    this.accessToken = accessToken;
    if (accessToken) {
      const user = jwtDecode(accessToken);
      this.accessTokenExp = (user.exp ?? 0) * 1000;
      this.onUserChange(user as any);
    } else {
      this.onUserChange(undefined);
    }
  };

  addInterceptors = () => {
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        let accessToken = this.accessToken;
        if (
          !accessToken ||
          this.accessTokenExp === 0 ||
          this.accessTokenExp < Date.now() - 60000
        ) {
          const refreshToken = this.refreshToken;
          if (!refreshToken) {
            return config;
          }
          const result = await this.refreshTokens();
          accessToken = result?.accessToken;
        }

        if (!accessToken) {
          return config;
        }

        config.headers.set('authorization', `Bearer ${accessToken}`);

        return config;
      },
      (error) => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        console.log(
          'Received Resp',
          response?.headers,
          response?.headers['x-refresh-token'] === 'true',
        );
        if (response?.headers['x-refresh-token'] === 'true') {
          this.refreshTokens().then();
        }
        return response;
      },
      async (error) => {
        const config = error?.config;

        if (
          error?.response?.status === 401 &&
          error?.response?.data?.code === 'TOKEN_EXPIRED' &&
          !config?.sent
        ) {
          config.sent = true;

          const result = await this.refreshTokens();

          if (result?.accessToken) {
            config.headers = {
              ...config.headers,
              authorization: `Bearer ${result?.accessToken}`,
            };
          }

          return axios(config);
        }
        return Promise.reject(error);
      },
    );
  };

  logout = () => {
    this.cookies.remove(this.accessTokenName);
    this.cookies.remove('refreshToken');
    return { refreshToken: undefined, accessToken: undefined };
  };

  refreshTokenFn = async () => {
    try {
      const response = await this.publicConnection.post(this.tokenRefreshPath, {
        refreshToken: this.refreshToken,
      });

      const { refreshToken, accessToken } = response.data;

      if (!accessToken) {
        return this.logout();
      }

      this.cookies.set(this.accessTokenName, accessToken);
      this.cookies.set('refreshToken', refreshToken);
      this.setAccessToken(accessToken);
      this.refreshToken = refreshToken;
      return { refreshToken, accessToken };
    } catch (error: any) {
      if (error?.response?.status === 401) {
        return this.logout();
      }
      throw error;
    }
  };
}
